import React from 'react';
import { HandIcon, XIcon } from 'lucide-react';
import { InitialAvatar } from '../InitialAvatar';
import { Button } from '../ui/Button';
import { BadgeMark } from '../profile/MemberBadges';
import { PrototypeNote } from '../prototype/PrototypeNote';
import { prototypeStateFor } from '../../data/prototypeStates';
import { AFFILIATION_LABELS, CURRENT_MEMBER, overlapWith, publicName } from '../../data/community';
import type { Member } from '../../data/community';
interface PersonDetailProps {
  member: Member | null;
  onClose: () => void;
  onOpenProfile: (member: Member) => void;
}
export function PersonDetail({
  member,
  onClose,
  onOpenProfile
}: PersonDetailProps) {
  if (!member) {
    return <div className="flex h-full flex-col justify-center gap-2 rounded-card border border-dashed border-rule p-6" data-id="element-269" data-mp="34ktdu8r6kayi">
        <p className="text-body text-ink" data-id="element-270" data-mp="16syv0isrsk6v">Pick anyone on the chart.</p>
        <p className="text-small text-ink-muted" data-id="element-271" data-mp="12s57vrn5j9f1">
          Bars that sit under your years belong to people who were here when you were.
        </p>
      </div>;
  }
  const isYou = member.name === CURRENT_MEMBER.fullName;
  const overlap = isYou ? null : overlapWith(member, CURRENT_MEMBER);
  const memorial = member.memorial;
  const note = prototypeStateFor(member.name);
  return <div className="flex h-full flex-col gap-5 rounded-card border border-rule bg-card p-5" data-id="element-272" data-mp="1jtqxvvrm6pop">
      <div className="flex items-start justify-between gap-3" data-id="element-273" data-mp="27jv2a5cmmtgr">
        <div className="flex items-center gap-3" data-id="element-274" data-mp="27kd5jlxpi7y6">
          <InitialAvatar name={member.name} size={48} data-id="element-275" data-mp="2cj7msjg0zog6" />
          <div className="flex flex-col" data-id="element-276" data-mp="2ckshc92cb7e1">
            <span className="flex items-center gap-1.5 text-heading font-semibold text-ink" data-id="element-277" data-mp="1yrt1g5ynbb5y">
              {isYou ? 'You' : publicName(member.name)}
              <BadgeMark name={member.name} data-id="element-278" data-mp="22r1ut9prx70z" />
            </span>
            <span className="text-small text-ink-muted" data-id="element-279" data-mp="2qhecvdeqgk5a">
              {member.years} · {AFFILIATION_LABELS[member.affiliation]}
            </span>
            {memorial ? <span className="mt-1 inline-flex w-fit items-center gap-1.5 text-eyebrow-caption font-semibold uppercase text-ink-muted" data-id="element-280" data-mp="355o6j3re0e90">
                <span aria-hidden="true" className="h-px w-4 bg-highlight" data-id="element-281" data-mp="3142m8xciwxos" />
                In memory
              </span> : null}
            {note ? <PrototypeNote className="mt-1.5 max-w-full" data-id="element-282" data-mp="2r5womrxr2dpq">{note.label}</PrototypeNote> : null}
          </div>
        </div>

        <button type="button" onClick={onClose} className="inline-flex h-11 w-11 items-center justify-center rounded-pill text-ink-muted hover:bg-canvas hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-283" data-mp="2m7v19utajs8g">
          <span className="sr-only" data-id="element-284" data-mp="28gq7vaginw6k">Close</span>
          <XIcon aria-hidden="true" className="h-4 w-4" data-id="element-285" data-mp="23gc4i5pv1aev" />
        </button>
      </div>

      {overlap ? <p className="rounded-field bg-primary-soft px-4 py-3 text-small text-primary-text" data-id="element-286" data-mp="1ygfxdxlv92v3">
          You were both here from{' '}
          <span className="font-semibold" data-id="element-287" data-mp="1ocj10pdoqi7f">
            {overlap.start}–{overlap.end}
          </span>
          .
        </p> : !isYou ? <p className="rounded-field bg-canvas px-4 py-3 text-small text-ink-muted" data-id="element-288" data-mp="1yb8abtrimuc2">
          Your years don&apos;t overlap — they were here{' '}
          {member.years.replace('–', ' to ')}.
        </p> : null}

      <dl className="flex flex-col gap-3 text-small" data-id="element-289" data-mp="15n2pz84dj7cr">
        {member.city ? <div className="flex justify-between gap-4 border-b border-rule pb-3" data-id="element-290" data-mp="1akupoob9fxri">
            <dt className="text-ink-muted" data-id="element-291" data-mp="1fkh4v3anfo2y">Where now</dt>
            <dd className="text-ink" data-id="element-292" data-mp="1fiw95acllz3i">{member.city}</dd>
          </div> : null}
        {member.note && !isYou ? <div className="flex justify-between gap-4 border-b border-rule pb-3" data-id="element-293" data-mp="1kda8toyamsdz">
            <dt className="text-ink-muted" data-id="element-294" data-mp="mva37vfweidh">Known for</dt>
            <dd className="text-right text-ink" data-id="element-295" data-mp="rucxckucb46o">{member.note}</dd>
          </div> : null}
        <div className="flex justify-between gap-4" data-id="element-296" data-mp="2tjiwtnjsuscq">
          <dt className="text-ink-muted" data-id="element-297" data-mp="2thzelrel278b">Contact</dt>
          <dd className="text-right text-ink-muted" data-id="element-298" data-mp="2olqtzyhm9ltr">Never shown to members</dd>
        </div>
      </dl>

      {note ? <PrototypeNote variant="block" data-id="element-299" data-mp="33awzg471s8a3">{note.note}</PrototypeNote> : null}

      {memorial ? <div className="mt-auto flex flex-col gap-3" data-id="element-300" data-mp="2yi3w9kr89804">
          <p className="rounded-field bg-canvas px-4 py-3 text-small text-ink-muted" data-id="element-301" data-mp="2yhc3ehp0lodj">
            {publicName(member.name)} died in {memorial.died}. Their page is a memorial — you can
            read it and add something you remember.
          </p>
          <Button variant="secondary" onClick={() => onOpenProfile(member)} data-id="element-302" data-mp="3m7odnz7qfx7a">
            Open their memorial
          </Button>
        </div> : !isYou ? <div className="mt-auto flex flex-wrap gap-2" data-id="element-303" data-mp="1ruggw2075bj0">
          <Button data-id="element-304" data-mp="1rv8ax8e5b176">
            <HandIcon aria-hidden="true" className="h-4 w-4" data-id="element-305" data-mp="1rwjhth2w0p2i" />
            Wave
          </Button>
          <Button variant="secondary" onClick={() => onOpenProfile(member)} data-id="element-306" data-mp="1mxq01cgkibwj">
            See profile
          </Button>
        </div> : <div className="mt-auto" data-id="element-307" data-mp="7cxliy224ex1">
          <Button variant="secondary" onClick={() => onOpenProfile(member)} data-id="element-308" data-mp="7bw84mxkjgc5">
            Your profile
          </Button>
        </div>}
    </div>;
}